/**
 * Page renderer. Pure — given a PageData, theme, and render context, returns
 * a complete HTML document as a string. No I/O, no React, no DOM. Safe to
 * call from a Worker or to inject into an iframe `srcdoc` for editor preview.
 */

import { getBlockDef } from './blocks';
import { escapeHtml } from './html';
import { normalizeTheme, themeCss } from './theme';
import type { Block, BlockType, PageData, PageMeta, RenderContext, ThemeConfig } from './types';

/**
 * Render ONE block's HTML in isolation — used by the editor canvas to draw
 * each block inline (so drag-and-drop works natively) while still using the
 * exact same `def.render(...)` the bouncer calls. Production parity stays
 * intact because the editor and the bouncer call the same code path.
 *
 * Returns '' on unknown type or render error (defensive — the editor
 * never crashes because of one bad block).
 */
export function renderBlockOnly(block: Block, theme: ThemeConfig, ctx: RenderContext): string {
  const def = getBlockDef(block.type);
  if (!def) return '';
  try {
    return def.render(block.props, theme, ctx);
  } catch {
    return '';
  }
}

/**
 * Theme as a React CSSProperties-style object — applied inline on the
 * canvas wrapper in the editor so theme CSS variables resolve correctly
 * without leaking to the surrounding dashboard chrome (we can't use
 * `:root { ... }` like the bouncer's <style> block does because the
 * editor is rendering inline in the dashboard's DOM).
 */
export function themeStyleVars(theme: ThemeConfig): Record<string, string> {
  const fontStack =
    theme.fontFamily === 'serif'
      ? 'ui-serif, Georgia, Cambria, "Times New Roman", Times, serif'
      : theme.fontFamily === 'mono'
        ? 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace'
        : theme.fontFamily === 'sf-pro'
          ? '"SF Pro Display", "SF Pro Text", -apple-system, BlinkMacSystemFont, system-ui, "Helvetica Neue", Arial, sans-serif'
          : '-apple-system, BlinkMacSystemFont, "Segoe UI", Inter, Roboto, "Helvetica Neue", Arial, sans-serif';

  const buttonRadius = theme.radius === 'sharp' ? '4px' : theme.radius === 'pill' ? '9999px' : '12px';
  const cardRadius = theme.radius === 'sharp' ? '4px' : theme.radius === 'pill' ? '24px' : '16px';

  return {
    '--primary': `#${theme.primaryColor}`,
    '--bg': `#${theme.backgroundColor}`,
    '--text': `#${theme.textColor}`,
    '--font': fontStack,
    '--radius-btn': buttonRadius,
    '--radius-card': cardRadius,
    background: `#${theme.backgroundColor}`,
    color: `#${theme.textColor}`,
    fontFamily: fontStack,
  };
}

/**
 * Coerce loosely-typed input (e.g. from D1's JSON column) into a clean
 * array of blocks. Drops anything malformed. Each block's own validator
 * runs at edit time via the API; this is the last line of defense at render.
 */
export function normalizeBlocks(raw: unknown): Block[] {
  if (!Array.isArray(raw)) return [];
  const out: Block[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const i = item as Partial<Block>;
    if (typeof i.id !== 'string' || typeof i.type !== 'string') continue;
    const def = getBlockDef(i.type as BlockType);
    if (!def) continue;
    let props;
    try {
      props = def.validate(i.props ?? {});
    } catch {
      continue;
    }
    out.push({ id: i.id, type: i.type as BlockType, props });
  }
  return out;
}

/**
 * Walk every block's props and return the set of distinct outbound URLs
 * (http/https only) that would be emitted as `<a href>` targets.
 *
 * Used by the bouncer's pre-render token-signing step — `tokenizeUrl`
 * needs to be a SYNCHRONOUS lookup (block.render() is sync) but HMAC
 * signing is async, so we walk first, sign all URLs in parallel, build
 * a Map, then expose the map's `.get` as `tokenizeUrl`. Cheap enough
 * (~0.1ms per sign) that we don't bother caching across requests.
 *
 * Greedy by design — scans every string-typed prop recursively. May
 * pick up URLs that aren't outbound (e.g. image `src` fields). That's
 * fine: blocks only USE the tokenizer for actual `<a href>` so the
 * extra signings are wasted bytes in the Map, not wrong behavior.
 */
export function extractOutboundUrls(blocks: Block[]): string[] {
  const urls = new Set<string>();
  const visit = (val: unknown): void => {
    if (typeof val === 'string') {
      // Only http(s) URLs — tokenizing mailto:/tel:/etc. is meaningless.
      if (/^https?:\/\//i.test(val.trim())) urls.add(val.trim());
    } else if (Array.isArray(val)) {
      for (const item of val) visit(item);
    } else if (val && typeof val === 'object') {
      for (const v of Object.values(val)) visit(v);
    }
  };
  for (const b of blocks) visit(b.props);
  return Array.from(urls);
}

export function normalizeMeta(raw: unknown): PageMeta {
  if (!raw || typeof raw !== 'object') return {};
  const i = raw as Partial<PageMeta>;
  return {
    title: typeof i.title === 'string' ? i.title.slice(0, 120) : undefined,
    description: typeof i.description === 'string' ? i.description.slice(0, 300) : undefined,
    ogImage: typeof i.ogImage === 'string' ? i.ogImage.slice(0, 2000) : undefined,
    favicon: typeof i.favicon === 'string' ? i.favicon.slice(0, 2000) : undefined,
  };
}

/**
 * Default favicon — the Poof cloud (💨). Inline SVG data URL so it works
 * on every TLD without needing to host an /favicon.ico per customer
 * domain. Customers can override by setting meta.favicon.
 *
 * Same icon the dashboard at app.poof.bio uses for its tab — keeps
 * Poof's identity consistent across product surfaces, AND every free-tier
 * page bouncing through IG also displays the cloud icon in the visitor's
 * browser tab. Tiny passive brand-presence on every visit.
 */
export const DEFAULT_POOF_FAVICON =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Ctext y='.9em' font-size='90'%3E%F0%9F%92%A8%3C/text%3E%3C/svg%3E";

const DEFAULT_BRAND_CHIP_HTML = `
<div class="pb-brand-chip" style="margin-top:36px;display:flex;justify-content:center;">
  <a href="https://poof.bio/?ref=footer" target="_blank" rel="noopener" style="display:inline-flex;align-items:center;gap:6px;padding:6px 10px;border-radius:9999px;background:rgba(0,0,0,0.04);color:var(--text);opacity:0.65;font-size:12px;text-decoration:none;font-family:var(--font);">
    Made with <strong style="color:var(--primary);">Poof</strong> <span>💨</span>
  </a>
</div>`;

/**
 * Render a full page to an HTML document string. Stable single-pass,
 * no streaming, no async.
 */
export function renderPage(
  page: { blocks: Block[]; theme?: Partial<ThemeConfig>; meta?: PageMeta; name?: string },
  ctx: RenderContext,
): string {
  const theme = normalizeTheme(page.theme ?? null);
  const css = themeCss(theme);
  // Each rendered block is wrapped in a stable `<div data-pb-block-id>`
  // so the click tracker (emitted at the end of the page) can attribute
  // outbound `<a>` clicks back to the originating block — that's how we
  // compute per-block CTR ("Latest drop got 32%, Newsletter got 11%").
  // Wrapping doesn't change the block's visual rendering because the
  // wrapper is `display:contents`.
  const blocksHtml = page.blocks
    .map((b) => {
      const def = getBlockDef(b.type);
      if (!def) return '';
      try {
        // Inject the block id into the props object as a magic
        // `__blockId` key. This lets blocks that need to coordinate
        // with the parent (currently: link-rotator → ctx.rotatorPicks)
        // know their own id at render time without changing the
        // BlockDefinition.render() signature. Other blocks ignore it.
        const propsWithId = { ...b.props, __blockId: b.id };
        const html = def.render(propsWithId, theme, ctx);
        return `<div class="pb-block-wrap" data-pb-block-id="${escapeHtml(b.id)}" data-pb-block-type="${escapeHtml(b.type)}" style="display:contents;">${html}</div>`;
      } catch {
        return '';
      }
    })
    .join('\n');

  // Public title is OPT-IN. Order: explicit meta.title → ctx.defaultTitle
  // (bouncer passes the link's short code on the real-lander path; omits
  // on the decoy path) → empty. Never derives from page.name — page.name
  // is an internal label only. A decoy named "Safe Decoy" leaking as
  // og:title landed an org in an IG restriction on 2026-05-19.
  // Empty title renders as <title></title> and suppresses og:title — see
  // the emission block below.
  const title = page.meta?.title || ctx.defaultTitle || '';
  const desc = page.meta?.description || '';
  const og = page.meta?.ogImage || '';
  // Favicon defaults to the Poof cloud — no page ever ends up with the
  // browser's empty-document icon. Customers can override per-page in
  // the editor under Theme → Browser tab icon.
  const favicon = page.meta?.favicon || DEFAULT_POOF_FAVICON;

  // Hero background image — when set, paints a tall image at the top of
  // the page with a vertical gradient fade to the solid background color.
  // Content scrolls over it. Pairs with backgroundOverlay (default ON
  // when a hero image is set) for legibility.
  const heroHtml = theme.backgroundImage
    ? `<div class="pb-hero" aria-hidden="true">
  <div class="pb-hero-image" style="background-image:url('${escapeHtml(theme.backgroundImage)}');"></div>
  ${theme.backgroundOverlay !== false ? `<div class="pb-hero-overlay"></div>` : ''}
</div>`
    : '';

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=5">
  <title>${escapeHtml(title)}</title>
  <link rel="icon" href="${escapeHtml(favicon)}">
  <link rel="apple-touch-icon" href="${escapeHtml(favicon)}">
  <!-- Anti-scrape / anti-index protections.
       The bouncer also sets these as HTTP headers (referrer-policy +
       x-robots-tag) — duplicating them here covers bots that only
       inspect the HTML body, not the response headers. -->
  <meta name="referrer" content="no-referrer">
  <meta name="robots" content="noindex, nofollow, noarchive, nosnippet, noimageindex, nocache">
  <meta name="googlebot" content="noindex, nofollow, noarchive, nosnippet, noimageindex">
  ${desc ? `<meta name="description" content="${escapeHtml(desc)}">` : ''}
  ${og ? `<meta property="og:image" content="${escapeHtml(og)}">` : ''}
  ${title ? `<meta property="og:title" content="${escapeHtml(title)}">` : ''}
  ${desc ? `<meta property="og:description" content="${escapeHtml(desc)}">` : ''}
  ${og || title || desc ? `<meta name="twitter:card" content="summary_large_image">` : ''}
  <style>
    ${css}
    *,*::before,*::after{box-sizing:border-box;}
    html,body{margin:0;padding:0;}
    body{background:var(--bg);color:var(--text);font-family:var(--font);min-height:100vh;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;}
    .pb-page{position:relative;max-width:520px;margin:0 auto;padding:32px 20px 48px;display:flex;flex-direction:column;gap:14px;}
    .pb-page > *{position:relative;z-index:1;}
    .pb-block{width:100%;}
    a{color:var(--primary);}
    /* Hero — absolutely positioned at the very top of the page,
       extends partway down (75vh) and fades into the solid bg color via
       the overlay so content remains legible. */
    .pb-hero{position:absolute;top:0;left:0;right:0;height:75vh;max-height:760px;overflow:hidden;z-index:0;pointer-events:none;}
    .pb-hero-image{position:absolute;inset:0;background-size:cover;background-position:center top;background-repeat:no-repeat;}
    .pb-hero-overlay{position:absolute;inset:0;background:linear-gradient(to bottom, transparent 0%, transparent 30%, var(--bg) 95%);}
    @media (max-width:380px){.pb-page{padding:24px 16px 40px;}}
  </style>
</head>
<body${ctx.variantId ? ` data-pb-variant-id="${escapeHtml(ctx.variantId)}"` : ''}>
  ${heroHtml}
  <main class="pb-page">
    ${blocksHtml}
    ${ctx.showBranding ? DEFAULT_BRAND_CHIP_HTML : ''}
  </main>
  ${ctx.tracking === false ? '' : CLICK_TRACKER_SCRIPT}
</body>
</html>`;
}

/**
 * Page-click tracker + URL hygiene — emitted at the end of the body.
 *
 *   1. Strip the query string from the visible URL via
 *      `history.replaceState`. Prevents UTM params (utm_source=instagram
 *      …) from leaking when the visitor copies the URL or shares it,
 *      and prevents downstream `<a>` clicks from inheriting our query
 *      string when destination pages read `document.referrer`. Done
 *      FIRST so even if the tracker setup throws, the URL is clean.
 *
 *   2. Click attribution — `navigator.sendBeacon` fires reliably even
 *      as the browser navigates away. Attaches a capture-phase click
 *      listener to every `<a>` inside every `[data-pb-block-id]`
 *      wrapper so we can identify which block was tapped.
 *
 * Small (~750 bytes minified), no external deps, swallow-and-move-on
 * errors so a broken beacon never blocks an outbound click.
 */
const CLICK_TRACKER_SCRIPT = `<script>(function(){try{if(location.search&&history.replaceState){history.replaceState(history.state,'',location.pathname+location.hash);}}catch(e){}if(!navigator.sendBeacon)return;var v=document.body.getAttribute('data-pb-variant-id')||'';function send(blockId,a){try{var p={b:blockId,h:location.hostname,p:location.pathname,v:v};if(a&&a.getAttribute){var ui=a.getAttribute('data-pb-url-index');if(ui!==null&&ui!==''){var n=parseInt(ui,10);if(n===n)p.i=n;}var ii=a.getAttribute('data-pb-item-index');if(ii!==null&&ii!==''){var m=parseInt(ii,10);if(m===m)p.it=m;}}var body=JSON.stringify(p);var blob=new Blob([body],{type:'application/json'});navigator.sendBeacon('/__click',blob);}catch(e){}}var wraps=document.querySelectorAll('[data-pb-block-id]');for(var i=0;i<wraps.length;i++){(function(el){var id=el.getAttribute('data-pb-block-id');if(!id)return;var as=el.querySelectorAll('a[href]');for(var j=0;j<as.length;j++){as[j].addEventListener('click',function(e){send(id,e.currentTarget);},{capture:true});}})(wraps[i]);}})();</script>`;
